import { ArrowRight, X } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DetailSection } from "@/components/shared/detail-section";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  type PersonDetail,
  type PersonPendingChange,
  useCancelPendingChange,
} from "@/hooks/use-persons";
import { formatEffectiveDate } from "@/lib/dates";

/**
 * Every not-yet-effective scheduled change for this person, in effective-date
 * order (WI-055 / WI-071 — stacked changes are all listed). Admins can cancel a
 * change before it takes effect; the cancellation is audited on the person's
 * change history. Renders nothing when the queue is empty.
 */
export function PersonScheduledChangesSection({
  person,
  isAdmin,
  pending,
  failed = false,
  onRetry,
}: {
  person: PersonDetail;
  isAdmin: boolean;
  pending: PersonPendingChange[];
  /** The read failed — say so rather than showing an empty section. */
  failed?: boolean;
  onRetry?: () => void;
}) {
  const cancelChange = useCancelPendingChange(person.id);
  const [cancelTarget, setCancelTarget] = useState<PersonPendingChange | null>(null);

  if (failed) {
    return (
      <DetailSection title="Scheduled changes" boldTitle>
        <ErrorState
          message="Scheduled changes couldn't be loaded — this doesn't mean there are none."
          onRetry={onRetry}
        />
      </DetailSection>
    );
  }
  if (pending.length === 0) return null;

  return (
    <DetailSection title="Scheduled changes" boldTitle>
      <ul className="space-y-2">
        {pending.map((change) => (
          <li
            key={change.id}
            className="flex items-start justify-between gap-2 rounded-md border bg-card px-3 py-2 text-sm"
          >
            <div className="min-w-0 space-y-0.5">
              <p className="font-medium text-foreground">{change.label}</p>
              <p className="flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
                <span>{change.oldValueLabel}</span>
                <ArrowRight className="inline h-3 w-3 shrink-0" />
                <span className="font-medium text-foreground">{change.newValueLabel}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                Effective {formatEffectiveDate(change.effectiveDate)}
                {change.actorName ? ` · scheduled by ${change.actorName}` : ""}
              </p>
            </div>
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0 text-muted-foreground hover:text-destructive"
                aria-label={`Cancel scheduled ${change.label} change`}
                onClick={() => setCancelTarget(change)}
              >
                <X className="size-3.5" />
              </Button>
            )}
          </li>
        ))}
      </ul>

      {cancelTarget && (
        <ConfirmDialog
          open
          onOpenChange={() => setCancelTarget(null)}
          title="Cancel scheduled change?"
          description={`${cancelTarget.label} will no longer change to “${cancelTarget.newValueLabel}” on ${formatEffectiveDate(cancelTarget.effectiveDate)}. The cancellation is recorded in the change history.`}
          cancelLabel="Keep it"
          confirmLabel="Cancel change"
          onConfirm={() => {
            cancelChange.mutate(cancelTarget.id);
            setCancelTarget(null);
          }}
        />
      )}
    </DetailSection>
  );
}
